import React, { useEffect, useState } from 'react';
import { Timeline, Dropdown, Menu, Icon, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { Comment, Header, NoAudit, Spinner, Wrapper } from './styles';
import { VERSIONS_AUDIT_TAB } from './translationValues';
import { getAuditDetails } from '../../../utils/services/complexVersionsAPI';
import { useCallback } from 'react';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../../context/complexDocumentContext';
import moment from 'moment';

const NAMESPACES = ['VERSIONS_AUDIT_TAB'];

const STATUS_LABELS = {
  published: VERSIONS_AUDIT_TAB.STATUS_PUBLISHED,
  qacomplete: VERSIONS_AUDIT_TAB.STATUS_SENT_FOR_APPROVAL,
  qaready: VERSIONS_AUDIT_TAB.STATUS_SENT_FOR_TESTING,
  rejected: VERSIONS_AUDIT_TAB.STATUS_REJECTED,
};

const STATUS_COLORS = {
  published: 'green',
  qacomplete: 'gold',
  qaready: 'gold',
  rejected: 'red',
}

const VersionsAudit = () => {
  const { t } = useTranslation(NAMESPACES);
  const [auditList, setAuditList] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [auditError, setAuditError] = useState(false);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, `VersionsAudit`);

  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(documentIdSelector, `VersionsAudit`);

  const isEmailTemplateSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE], []);
  const [isEmailTemplate] = useDocumentContext(isEmailTemplateSelector, `VersionsAudit`);

  const fetchAuditList = useCallback(async () => {
    setAuditError(false);
    setLoading(true);
    let type = currentDesign;
    if (isEmailTemplate) {
      type = currentDesign === 'block' ? 'email_block' : 'email';
    }
    try {
      const response = await getAuditDetails(documentId, type, sortOrder);

      if (response?.statusCode === 5610) {
        setAuditList(response.data || []);
      } else if (response?.statusCode === 5611) {
        setAuditError(true);
        console.error(t(
          VERSIONS_AUDIT_TAB.TEMPLATE_NOT_MOVED.key,
          VERSIONS_AUDIT_TAB.TEMPLATE_NOT_MOVED.value
        ));
      } else {
        message.error(t(
          VERSIONS_AUDIT_TAB.AUDIT_FETCH_FAIL.key,
          VERSIONS_AUDIT_TAB.AUDIT_FETCH_FAIL.value
        ));
      }
    } catch (err) {
      console.error('Error fetching audit list:', err);
      message.error(t(
        VERSIONS_AUDIT_TAB.AUDIT_FETCH_FAIL.key,
        VERSIONS_AUDIT_TAB.AUDIT_FETCH_FAIL.value
      ));
    } finally {
      setLoading(false);
    }
  }, [documentId, currentDesign, sortOrder, isEmailTemplate]);

  useEffect(() => {
    fetchAuditList();
  }, [fetchAuditList]);

  const handleSortChange = order => setSortOrder(order);

  const sortMenu = (
    <Menu>
      <Menu.Item key="desc" onClick={() => handleSortChange('desc')}>
        {t(VERSIONS_AUDIT_TAB.MOST_RECENT_LABEL.key, VERSIONS_AUDIT_TAB.MOST_RECENT_LABEL.value)}
      </Menu.Item>
      <Menu.Item key="asc" onClick={() => handleSortChange('asc')}>
        {t(VERSIONS_AUDIT_TAB.OLDEST_LABEL.key, VERSIONS_AUDIT_TAB.OLDEST_LABEL.value)}
      </Menu.Item>
    </Menu>
  );

  return (
    <Wrapper>
      {
        auditList.length > 0 &&
        <Header>
          <Dropdown overlay={sortMenu} placement="bottomRight" trigger={['click']}>
            <div className="sort-dropdown">
              {sortOrder === 'desc'
                ? t(VERSIONS_AUDIT_TAB.MOST_RECENT_LABEL.key, VERSIONS_AUDIT_TAB.MOST_RECENT_LABEL.value)
                : t(VERSIONS_AUDIT_TAB.OLDEST_LABEL.key, VERSIONS_AUDIT_TAB.OLDEST_LABEL.value)}
              <Icon type="right" />
            </div>
          </Dropdown>
        </Header>
      }

      {loading ? (
        <Spinner tip={t(VERSIONS_AUDIT_TAB.LOADING_LABEL.key, VERSIONS_AUDIT_TAB.LOADING_LABEL.value)} />
      ) : (
        auditList.length > 0 ?
          <Timeline>
            {auditList.map((item, index) => {
              const localizedStatus = STATUS_LABELS[item.status] || { key: item.status, value: item.status };
              return (
                <Timeline.Item key={index} color={STATUS_COLORS[item.status]}>
                  <div style={{ color: '#fff' }}>
                    <strong>{t(localizedStatus.key, localizedStatus.value)}</strong>
                    <br />
                    <span style={{ color: '#bbb', fontSize: '13px', fontStyle: 'italic' }}>
                      {t(VERSIONS_AUDIT_TAB.BY_LABEL.key, VERSIONS_AUDIT_TAB.BY_LABEL.value)} {item.doneBy}{' '}
                      {t(VERSIONS_AUDIT_TAB.ON_LABEL.key, VERSIONS_AUDIT_TAB.ON_LABEL.value)} {moment(item.doneOn).format('lll')}
                    </span>
                    {item.rejectedComments && (
                      <Comment>
                        {t(VERSIONS_AUDIT_TAB.COMMENTS_LABEL.key, VERSIONS_AUDIT_TAB.COMMENTS_LABEL.value)}{' '}
                        {item.rejectedComments}
                      </Comment>
                    )}
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
          : <NoAudit>
           {
            auditError ? t(VERSIONS_AUDIT_TAB.TEMPLATE_AUDIT_NOT_AVAILABLE.key, VERSIONS_AUDIT_TAB.TEMPLATE_AUDIT_NOT_AVAILABLE.value) :
            t(VERSIONS_AUDIT_TAB.AUDIT_INFO_NOT_AVAILABLE.key, VERSIONS_AUDIT_TAB.AUDIT_INFO_NOT_AVAILABLE.value)
           } 
          </NoAudit>
      )}
    </Wrapper>
  );
};

export default VersionsAudit;
